import {
    DistributorReference,
    Filter,
    ItemsForUserVO,
    ItemsWarehouseVO,
    Reference,
    WarehouseInventories
} from "../../models/table-models";
import { Pagination } from "../../utils/pagination-hooks";
import { useTranslation } from "react-i18next";
import { formatQuantitiesByPassport } from "../../utils/helper-functions";
import { Accordion, Button } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import { NoDataComponent } from "../../components/NoDataComponent";
import React, { useMemo } from "react";
import { CustomTable } from "../../components/CustomTable";
import XLSX from "xlsx";
import FileSaver from "file-saver";
import {
    referencesDistributorTableHeaders,
    referencesInnerTableHeaders,
    referencesInnerWarehouseDelivers,
    referencesInnerWarehouseHandOut,
    referencesInnerWarehouseScraps,
    referencesTableHeaders,
    referencesWarehouseDelivers,
    referencesWarehouseHandOut,
    referencesWarehouseScraps
} from "../../utils/Enums/TableHeaders";
import { DistributorItems } from "./UserList";
import { Role } from "../../utils/Enums/Roles";

function sumByPassportAndBatch<T extends { passportId?: number; batchCode?: string; count: number }>(
    lists: T[][]
): T[] {
    const map = new Map<string, T>();
    const key = (x: T) => `${x.passportId ?? ""}::${x.batchCode ?? ""}`;
    for (const arr of lists) {
        for (const item of arr) {
            const k = key(item);
            const prev = map.get(k);
            if (prev) {
                prev.count += item.count;
                if ((item as any).oilAmount != null) {
                    (prev as any).oilAmount = ((prev as any).oilAmount ?? 0) + (item as any).oilAmount;
                }
            } else {
                map.set(k, { ...item });
            }
        }
    }
    return Array.from(map.values());
}

function WarehouseQuantities({ items, title }: { items: Reference[]; title: string }) {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const formatQuantitiesByPassportReference = (items: Reference[]) =>
        formatQuantitiesByPassport(
            items,
            (item: Reference) => item.passportName,
            (existing: Reference, next: Reference) => {
                existing.count += next.count;
                existing.collectionOfItems?.push(next);
            }
        );

    return (
        <>
            {items && items.length > 0 && (
                <div className='m-3'>
                    <h5 className='mb-2 d-flex justify-content-center w-100'>{t(title)}</h5>
                    <CustomTable
                        columns={{
                            headings: referencesTableHeaders,
                            collectionOfItems: items,
                            collapsedHeadings: referencesInnerTableHeaders,
                            innerTableFields: [{ field: 'passportName' }, { field: 'count' }, { field: 'batchCode' }],
                            groupByField: (item) => item.passportName,
                            items: formatQuantitiesByPassportReference(items).map(({ passportName, count }) => [passportName, count])
                        }}
                        accessEditRoles={[Role.WAREHOUSE]}
                        accessActionRoles={[Role.WAREHOUSE]}
                        tableData={formatQuantitiesByPassportReference(items)}
                        hasAccordionRow
                        tableFooterText={(item) => `${t('quantity')}: ${item.count.toString()}`}
                    />
                </div>
            )}
        </>
    );
}

function CompletedHandouts({
                               items,
                               formatQuantitiesByPassportVO
                           }: {
    items: ItemsWarehouseVO[];
    formatQuantitiesByPassportVO: (items: ItemsWarehouseVO[]) => ItemsWarehouseVO[];
}) {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    return (
        <>
            {items && items.length > 0 && (
                <div className='m-3'>
                    <h5 className='mb-2 d-flex justify-content-center text-center w-100'>{t('completedHandouts')}</h5>
                    <CustomTable
                        columns={{
                            headings: referencesWarehouseHandOut,
                            collapsedHeadings: referencesInnerWarehouseHandOut,
                            groupByField: (item) => item.passportName,
                            collectionOfItems: items,
                            innerTableFields: [
                                { field: 'passportName' },
                                { field: 'batchCode' },
                                { field: 'count' },
                                { field: 'warehouseUser' },
                                { field: 'receiver' }
                            ],
                            items: formatQuantitiesByPassportVO(items).map(({
                                                                                passportName,
                                                                                count
                                                                            }) => [passportName, count])
                        }}
                        hasAccordionRow
                        tableData={formatQuantitiesByPassportVO(items)}
                        tableFooterText={(item) => `${t('quantity')}: ${item.count.toString()}`}
                    />
                </div>
            )}
        </>
    );
}

function Scraps({
                    items,
                    formatQuantitiesByPassportVO
                }: {
    items: ItemsWarehouseVO[];
    formatQuantitiesByPassportVO: (items: ItemsWarehouseVO[]) => ItemsWarehouseVO[];
}) {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    return (
        <>
            {items && items.length > 0 && (
                <div className='m-3'>
                    <h5 className='mb-2 d-flex justify-content-center text-center w-100'>{t('scraps')}</h5>
                    <CustomTable
                        columns={{
                            headings: referencesWarehouseScraps,
                            collapsedHeadings: referencesInnerWarehouseScraps,
                            groupByField: (item) => item.passportName,
                            collectionOfItems: items,
                            innerTableFields: [
                                { field: 'passportName' },
                                { field: 'batchCode' },
                                { field: 'count' },
                                { field: 'receiver' }
                            ],
                            items: formatQuantitiesByPassportVO(items).map(({
                                                                                passportName,
                                                                                count
                                                                            }) => [passportName, count])
                        }}
                        tableData={formatQuantitiesByPassportVO(items)}
                        hasAccordionRow
                        tableFooterText={(item) => `${t('quantity')}: ${item.count.toString()}`}
                    />
                </div>
            )}
        </>
    );
}

function Delivers({
                      items,
                      formatQuantitiesByPassportVO
                  }: {
    items: ItemsWarehouseVO[];
    formatQuantitiesByPassportVO: (items: ItemsWarehouseVO[]) => ItemsWarehouseVO[];
}) {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    return (
        <>
            {items && items.length > 0 && (
                <div className='m-3'>
                    <h5 className='mb-2 d-flex justify-content-center text-center w-100'>{t('delivers')}</h5>
                    <CustomTable
                        columns={{
                            headings: referencesWarehouseDelivers,
                            collapsedHeadings: referencesInnerWarehouseDelivers,
                            groupByField: (item) => item.passportName,
                            collectionOfItems: items,
                            innerTableFields: [
                                { field: 'passportName' },
                                { field: 'batchCode' },
                                { field: 'count' },
                                { field: 'warehouseUser' }
                            ],
                            items: formatQuantitiesByPassportVO(items).map(({ passportName, count }) => [passportName, count])
                        }}
                        hasAccordionRow
                        tableData={formatQuantitiesByPassportVO(items)}
                        tableFooterText={(item) => `${t('quantity')}: ${item.count.toString()}`}
                    />
                </div>
            )}
        </>
    );
}

/* ---------- Main component ---------- */

export const WarehouseReferences = ({
                                        items,
                                        pagination,
                                        filter,
                                        distributorDays,
                                        showAll
                                    }: {
    warehouse?: string;
    items: WarehouseInventories[];
    filter: Filter;
    pagination: Pagination;
    refetchEntitiesHook: () => void;
    distributorDays?: ItemsForUserVO[];
    showAll?: boolean;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tTable } = useTranslation('translation', { keyPrefix: 'table' });
    const formatQuantitiesByPassportVO = (items: ItemsWarehouseVO[]) =>
        formatQuantitiesByPassport(
            items,
            (item) => item.passportId,
            (existingItem, newItem) => {
                existingItem.count += newItem.count;
                existingItem.collectionOfItems?.push(newItem);
            }
        );

    const usedByDate = useMemo(() => {
        const map = new Map<string, DistributorReference[]>();
        (distributorDays ?? []).forEach((day: any) => {
            if (!day?.date || !Array.isArray(day.usedItems)) return;
            const bucket = map.get(day.date) ?? [];
            bucket.push(...day.usedItems);
            map.set(day.date, bucket);
        });
        return map;
    }, [distributorDays]);

    const soldByDate = useMemo(() => {
        const map = new Map<string, DistributorReference[]>();
        (distributorDays ?? []).forEach((day: any) => {
            if (!day?.date || !Array.isArray(day.soldItems)) return;
            const bucket = map.get(day.date) ?? [];
            bucket.push(...day.soldItems);
            map.set(day.date, bucket);
        });
        return map;
    }, [distributorDays]);

    /* ---- Totals for used items across selected range ---- */
    const totalUsed = useMemo(() => {
        const lists = Array.from(usedByDate.values());
        // Add used items from warehouse data
        (items ?? []).forEach(d => {
            if (d.usedItems?.length) {
                lists.push(d.usedItems);
            }
        });
        return sumByPassportAndBatch(lists) as unknown as DistributorReference[];
    }, [usedByDate, items]);

    const totalSold = useMemo(() => {
        const lists = Array.from(soldByDate.values());
        (items ?? []).forEach(d => {
            if (d.soldItems?.length) {
                lists.push(d.soldItems);
            }
        });
        return sumByPassportAndBatch(lists) as unknown as DistributorReference[];
    }, [soldByDate, items]);
    // console.log(usedByDate);
    // console.log(totalUsed);

    // Build totals across the whole date range for this warehouse tab
    const allHandouts = (items ?? []).map((d) => d.itemsHandOut ?? []);
    const allScraps = (items ?? []).map((d) => d.scraps ?? []);
    const allDeliveries = (items ?? []).map((d) => d.deliveries ?? []);

    const totalHandouts = sumByPassportAndBatch(allHandouts);
    const totalScraps = sumByPassportAndBatch(allScraps);
    const totalDeliveries = sumByPassportAndBatch(allDeliveries);

    const formatQuantitiesByPassportDR = (items: DistributorReference[]) =>
        formatQuantitiesByPassport(
            items,
            (item) => item.passportId,
            (existingItem, newItem) => {
                existingItem.count += newItem.count;
                if (newItem.oilAmount != null) {
                    existingItem.oilAmount = (existingItem.oilAmount ?? 0) + newItem.oilAmount;
                }
                existingItem.collectionOfItems?.push(newItem);
            }
        );

    const exportHeaders = [...referencesDistributorTableHeaders.map((header) => tTable(header)), 'ml'];

    const calculateColumnWidths = (headers: string[], data: any[][]) => {
        return headers.map((header, colIndex) => {
            const maxContentWidth = Math.max(
                header.length,
                ...data.map(row => (row[colIndex] ? row[colIndex].toString().length : 0))
            );
            return { wch: Math.max(10, maxContentWidth + 2) };
        });
    };

    const buildReferenceExportSheet = (data: DistributorReference[]) => {
        const formattedData = formatQuantitiesByPassportDR(data).map(({ passportName, state, count, oilAmount }) => [
            passportName,
            t(state ? state : ''),
            count,
            oilAmount ?? '',
        ]);
        const ws = XLSX.utils.aoa_to_sheet([exportHeaders, ...formattedData]);
        ws["!cols"] = calculateColumnWidths(exportHeaders, formattedData);
        return ws;
    };

    const exportTotalReferences = () => {
        const usedSheetName = t('usedItems');
        const soldSheetName = t('soldItems');
        const wb = {
            Sheets: {
                [usedSheetName]: buildReferenceExportSheet(totalUsed),
                [soldSheetName]: buildReferenceExportSheet(totalSold),
            },
            SheetNames: [usedSheetName, soldSheetName],
        };
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        });
        FileSaver.saveAs(data, `references-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <>
            {items &&
                (items.length > 0 ? (
                    <>
                        <Accordion className='tasks accordion flex-grow-1 w-100' defaultActiveKey={[]} flush>
                            {items.map((data, index) =>
                                data.date != null ? (
                                    <Accordion.Item eventKey={String(index)} key={index} className='w-100 py-0'>
                                        <Accordion.Header className='w-100 p-1'>
                                            <div className='d-flex align-items -center text-center w-100'>
                                                <h5 className='mb-0 px-3'>{data.date}</h5>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className='d-flex pb-3 pt-1 w-100'>
                                            <div className='row g-0'>
                                                {showAll && (
                                                <div className='col-6 w-auto'>
                                                    <WarehouseQuantities
                                                        {...{ pagination, filter }}
                                                        items={data.warehouseQuantities}
                                                        title={'warehouseQuantities'}
                                                    />
                                                </div>
                                                )}
                                                {showAll && (
                                                <div className='col-6 w-auto'>
                                                    <WarehouseQuantities
                                                        {...{ pagination, filter }}
                                                        items={data.warehouseQuantitiesPreviousDay}
                                                        title={'warehouseQuantitiesPreviousDay'}
                                                    />
                                                </div>
                                                )}
                                                {showAll && (
                                                <div className='col-6 w-auto'>
                                                    <CompletedHandouts
                                                        {...{ pagination, filter }}
                                                        items={data.itemsHandOut}
                                                        formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                                    />
                                                </div>
                                                )}
                                                {showAll && (
                                                <div className='col-6 w-auto'>
                                                    <Scraps
                                                        {...{ pagination, filter }}
                                                        items={data.scraps}
                                                        formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                                    />
                                                </div>
                                                )}
                                                {showAll && (
                                                <div className='col-6 w-auto'>
                                                    <Delivers
                                                        {...{ pagination, filter }}
                                                        items={data.deliveries}
                                                        formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                                    />
                                                </div>
                                                )}
                                                {(usedByDate.get(data.date)?.length || data.usedItems?.length) ? (
                                                    <div className="col-6 w-auto">
                                                        <DistributorItems
                                                            list={[...(usedByDate.get(data.date) || []), ...(data.usedItems || [])]}
                                                            refetchEntitiesHook={() => {
                                                            }}
                                                            heading={"usedItems"}
                                                        />
                                                    </div>
                                                ) : null}
                                                {(soldByDate.get(data.date)?.length || data.soldItems?.length) ? (
                                                    <div className="col-6 w-auto">
                                                        <DistributorItems
                                                            list={[...(soldByDate.get(data.date) || []), ...(data.soldItems || [])]}
                                                            refetchEntitiesHook={() => {
                                                            }}
                                                            heading={"soldItems"}
                                                        />
                                                    </div>
                                                ) : null}
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ) : (
                                    <div className='row g-0' key={index}>
                                        {showAll && (
                                        <div className='col-6 w-auto'>
                                            <WarehouseQuantities
                                                {...{ pagination, filter }}
                                                items={data.warehouseQuantities}
                                                title={'warehouseQuantities'}
                                            />
                                        </div>
                                        )}
                                        {showAll && (
                                        <div className='col-6 w-auto'>
                                            <CompletedHandouts
                                                {...{ pagination, filter }}
                                                items={data.itemsHandOut}
                                                formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                            />
                                        </div>
                                        )}
                                        {showAll && (
                                        <div className='col-6 w-auto'>
                                            <Delivers
                                                {...{ pagination, filter }}
                                                items={data.deliveries}
                                                formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                            />
                                        </div>
                                        )}
                                        {showAll && (
                                        <div className='col-6 w-auto'>
                                            <Scraps
                                                {...{ pagination, filter }}
                                                items={data.scraps}
                                                formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                            />
                                        </div>
                                        )}
                                        <div className="col-6 w-auto">
                                            <DistributorItems
                                                list={totalUsed as unknown as DistributorReference[]}
                                                refetchEntitiesHook={() => {
                                                }}
                                                heading={"usedItems"}
                                                {...{ pagination, filter }}
                                            />
                                        </div>
                                        <div className="col-6 w-auto">
                                            <DistributorItems
                                                list={totalSold as unknown as DistributorReference[]}
                                                refetchEntitiesHook={() => {
                                                }}
                                                heading={"soldItems"}
                                                {...{ pagination, filter }}
                                            />
                                        </div>
                                    </div>
                                )
                            )}
                        </Accordion>
                        {/* --------- TOTAL for selected date range --------- */}
                        {(totalHandouts.length || totalScraps.length || totalDeliveries.length || totalUsed.length || totalSold.length) ? (
                            <Card className='w-100 mt-3'>
                                <Card.Header className='w-100 p-1'>
                                    <div className='d-flex align-items-center w-100'>
                                        {/* left-aligned, like the date rows */}
                                        <h5 className='mb-0 px-3'>{t('total')}</h5>
                                        <Button
                                            variant='success'
                                            className='ms-auto me-2'
                                            onClick={exportTotalReferences}
                                            disabled={!totalUsed.length && !totalSold.length}
                                        >
                                            {t('exportExcel')}
                                        </Button>
                                    </div>
                                </Card.Header>

                                <Card.Body className='pb-3 pt-1'>
                                    <div className='row g-0'>
                                        {showAll && (
                                        <div className='col-6 w-auto'>
                                            <CompletedHandouts
                                                {...{ pagination, filter }}
                                                items={totalHandouts}
                                                formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                            />
                                        </div>
                                        )}
                                        {showAll && (
                                        <div className='col-6 w-auto'>
                                            <Scraps
                                                {...{ pagination, filter }}
                                                items={totalScraps}
                                                formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                            />
                                        </div>
                                        )}
                                        {showAll && (
                                        <div className='col-6 w-auto'>
                                            <Delivers
                                                {...{ pagination, filter }}
                                                items={totalDeliveries}
                                                formatQuantitiesByPassportVO={formatQuantitiesByPassportVO}
                                            />
                                        </div>
                                        )}
                                        <div className="col-6 w-auto">
                                            <DistributorItems
                                                list={totalUsed as unknown as DistributorReference[]}
                                                refetchEntitiesHook={() => {
                                                }}
                                                heading={"usedItems"}
                                                {...{ pagination, filter }}
                                            />
                                        </div>
                                        <div className="col-6 w-auto">
                                            <DistributorItems
                                                list={totalSold as unknown as DistributorReference[]}
                                                refetchEntitiesHook={() => {
                                                }}
                                                heading={"soldItems"}
                                                {...{ pagination, filter }}
                                            />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        ) : null}
                    </>
                ) : (
                    <NoDataComponent noDataText={t('noInventoryReference')}/>
                ))}
        </>
    );
};
